import { Button, Modal } from '@bit-design/react'
import { Ds3CheckmarkIcon } from '@bit/icons-react'
import { openKyc } from '@bit/js-bridge'
import classNames from 'classnames'
import { useMemo } from 'react'

import { ActivateTaskStatusRes } from 'src/api/dashboard/getActivateTaskStatus'
import { useBoolean } from 'src/hook/base/useBoolean'
import { useMemoizedFn } from 'src/hook/base/useMemoizedFn'
import { useRouter } from 'src/hook/biz/useRouter'
import { useTranslations } from 'src/hook/biz/useTranslations'
import { checkIsApp } from 'src/util/app/checkIsApp'
import { getOfficialOriginUrl } from 'src/util/getOfficialOriginUrl'
import { handleCopyText } from 'src/util/handleCopyText'
import { openSafeWindow } from 'src/util/openSafeWindow'
import { gotoRouter } from 'src/util/router/gotoRouter'

import { GlobalModalRenderProps } from '../../type/service'
import { clickTrack } from './track/clickTrack'

const CheckBoxRadio: React.FC<{
  checked?: boolean
}> = ({ checked }) => {
  return (
    <div
      className={classNames(
        'flex flex-shrink-0 justify-center items-center w-[20px] h-[20px] border border-solid border-[#DFE0E2] rounded-[50%]',
        {
          ['bg-[var(--ds-color-always-black)] border-[var(--ds-color-always-black)]']: checked
        }
      )}
    >
      {checked && (
        <Ds3CheckmarkIcon className="w-[12px] h-[12px] text-[var(--ds-color-always-white)]" />
      )}
    </div>
  )
}

export const ActivationModal: React.FC<GlobalModalRenderProps<{ task: ActivateTaskStatusRes }>> = ({
  task,
  onFinish
}) => {
  const t = useTranslations()

  const [open, { setFalse }] = useBoolean(true)

  const router = useRouter()

  const isApp = useMemo(() => {
    return checkIsApp()
  }, [])

  // 点击立即认证
  const onClickAuth = useMemoizedFn(() => {
    clickTrack.clickB11379()

    if (isApp) {
      return openKyc({})
    }

    openSafeWindow(getOfficialOriginUrl('/verified/enter'))
  })

  // 点击复制链接
  const onCopyInviteLink = useMemoizedFn(() => {
    clickTrack.clickB11380()

    handleCopyText(t, task.defaultShortLink, t('common.copy_invite_link'))

    if (!isApp) {
      return
    }

    setFalse()

    const element = document.querySelector<HTMLDivElement>('#agency-expansion')

    if (!element) {
      return
    }

    window.scrollTo({
      top: element.offsetTop,
      behavior: 'smooth'
    })
  })

  // 点击查看手册
  const onClickManual = useMemoizedFn(() => {
    clickTrack.clickB11381()

    openSafeWindow(getOfficialOriginUrl('/academy/new-affiliate-guide'))
  })

  // 点击查看权益
  const onClickLevelRights = useMemoizedFn(() => {
    setFalse()

    router.push(gotoRouter('/personal-center'))
  })

  return (
    <Modal
      title={t('layout.activation_task_modal.title')}
      footer={
        <>
          {!isApp && (
            <div className="flex gap-[12px] w-full">
              <Button block size="medium" onClick={onClickManual}>
                {t('layout.activation_task_modal.footer_btn_left')}
              </Button>
              <Button
                block
                size="medium"
                type="main"
                onClick={onClickLevelRights}
                className="!ms-0"
              >
                {t('layout.activation_task_modal.footer_btn_right')}
              </Button>
            </div>
          )}

          {isApp && (
            <Button block size="medium" type="main" onClick={setFalse}>
              {t('login.cancel')}
            </Button>
          )}
        </>
      }
      open={open}
      onCancel={setFalse}
      maskClosable={false}
      afterClose={onFinish}
    >
      <div className="mb-[24px] text-[var(--ds-color-text-secondary)]">
        {t('layout.activation_task_modal.content_one_01')}
      </div>
      <div>
        <div className="flex gap-[12px] mb-[20px]">
          <CheckBoxRadio checked={task.kycKybStatus} />
          <div className="flex-1">
            <div className="mb-[4px] text-[var(--ds-color-text-primary)] font-[500]">
              {t('layout.activation_task_modal.task_1_title')}
            </div>
            <div className="text-[12px] text-[var(--ds-color-text-tertiary)]">
              {t('layout.activation_task_modal.task_1_des')}
            </div>
          </div>
          {!task.kycKybStatus && (
            <Button type="main" size="small" onClick={onClickAuth} className="self-center">
              {t('fd_broker.immediately_authenticate')}
            </Button>
          )}
        </div>
        <div className="flex gap-[12px] mb-[20px]">
          <CheckBoxRadio checked={task.newUserTaskFinish} />
          <div className="flex-1">
            <div className="mb-[4px] text-[var(--ds-color-text-primary)] font-[500]">
              {t('layout.activation_task_modal.task_2_title')}
            </div>
            <div className="text-[12px] text-[var(--ds-color-text-tertiary)]">
              {t('layout.activation_task_modal.task_2_content')}
            </div>
          </div>
          {!task.newUserTaskFinish && (
            <Button type="main" size="small" className="self-center" onClick={onCopyInviteLink}>
              {t('layout.activation_task_modal.task_2_btn')}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  )
}
